import React, { useState, useEffect } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Star,
  StarOff,
  ArrowUp,
  ArrowDown,
  Save,
  X,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import {
  getAllTopupsForAdmin,
  createTopup,
  updateTopup,
  deleteTopup,
  reorderTopups,
  fixDuplicateDisplayOrders
} from '../utils/topupDatabase';
import { formatCurrency } from '../utils/subscriptionCalculations';

const TopupManager = () => {
  const [topups, setTopups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingTopup, setEditingTopup] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    currency: '₹',
    category: 'operations',
    displayOrder: 999,
    isActive: true,
    popular: false,
    limits: {
      firebase_operations: 0,
      storage_mb: 0,
      sales_documents: 0,
      parties: 0
    },
    features: []
  });

  const categories = [
    { value: 'operations', label: 'Operations' },
    { value: 'storage', label: 'Storage' },
    { value: 'documents', label: 'Documents' },
    { value: 'parties', label: 'Parties' },
    { value: 'combo', label: 'Combo' },
    { value: 'enterprise', label: 'Enterprise' },
    { value: 'general', label: 'General' }
  ];

  useEffect(() => {
    loadTopups();
  }, []);

  const loadTopups = async () => {
    try {
      setLoading(true);
      
      // Fix any duplicate display orders first
      await fixDuplicateDisplayOrders();
      
      const topupsData = await getAllTopupsForAdmin();
      setTopups(topupsData);
    } catch (error) {
      console.error('Error loading top-ups:', error);
      setMessage({ type: 'error', text: 'Failed to load top-ups' });
    } finally {
      setLoading(false);
    }
  };


  const handleCreateTopup = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      // Validate form data
      if (!formData.name || !formData.price) {
        setMessage({ type: 'error', text: 'Name and price are required' });
        return;
      }

      const topupData = {
        ...formData,
        price: parseFloat(formData.price),
        displayOrder: parseInt(formData.displayOrder),
        limits: {
          firebase_operations: parseInt(formData.limits.firebase_operations) || 0,
          storage_mb: parseInt(formData.limits.storage_mb) || 0,
          sales_documents: parseInt(formData.limits.sales_documents) || 0,
          parties: parseInt(formData.limits.parties) || 0
        }
      };

      await createTopup(topupData);
      setMessage({ type: 'success', text: 'Top-up created successfully' });
      setShowCreateForm(false);
      resetForm();
      loadTopups();
    } catch (error) {
      console.error('Error creating top-up:', error);
      setMessage({ type: 'error', text: 'Failed to create top-up' });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTopup = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      const updateData = {
        ...formData,
        price: parseFloat(formData.price),
        displayOrder: parseInt(formData.displayOrder),
        limits: {
          firebase_operations: parseInt(formData.limits.firebase_operations) || 0,
          storage_mb: parseInt(formData.limits.storage_mb) || 0,
          sales_documents: parseInt(formData.limits.sales_documents) || 0,
          parties: parseInt(formData.limits.parties) || 0
        }
      };

      await updateTopup(editingTopup.id, updateData);
      setMessage({ type: 'success', text: 'Top-up updated successfully' });
      setEditingTopup(null);
      resetForm();
      loadTopups();
    } catch (error) {
      console.error('Error updating top-up:', error);
      setMessage({ type: 'error', text: 'Failed to update top-up' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTopup = async (topupId) => {
    try {
      setLoading(true);
      await deleteTopup(topupId);
      setMessage({ type: 'success', text: 'Top-up deleted successfully' });
      loadTopups();
    } catch (error) {
      console.error('Error deleting top-up:', error);
      setMessage({ type: 'error', text: 'Failed to delete top-up' });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (topup) => {
    try {
      setLoading(true);
      await updateTopup(topup.id, { isActive: !topup.isActive });
      setMessage({ type: 'success', text: `Top-up ${topup.isActive ? 'deactivated' : 'activated'} successfully` });
      loadTopups();
    } catch (error) {
      console.error('Error toggling top-up status:', error);
      setMessage({ type: 'error', text: 'Failed to update top-up status' });
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePopular = async (topup) => {
    try {
      setLoading(true);
      await updateTopup(topup.id, { popular: !topup.popular });
      setMessage({ type: 'success', text: `Top-up ${topup.popular ? 'removed from' : 'added to'} popular list` });
      loadTopups();
    } catch (error) {
      console.error('Error toggling popular status:', error);
      setMessage({ type: 'error', text: 'Failed to update popular status' });
    } finally {
      setLoading(false);
    }
  };

  const handleReorder = async (topupId, direction) => {
    try {
      const topup = topups.find(t => t.id === topupId);
      if (!topup) return;

      const newOrder = direction === 'up' ? topup.displayOrder - 1 : topup.displayOrder + 1;
      
      // Find the top-up that should swap positions
      const swapTopup = topups.find(t => t.displayOrder === newOrder);
      if (!swapTopup) return;

      await reorderTopups([
        { topupId: topup.id, displayOrder: newOrder },
        { topupId: swapTopup.id, displayOrder: topup.displayOrder }
      ]);

      loadTopups();
    } catch (error) {
      console.error('Error reordering top-ups:', error);
      setMessage({ type: 'error', text: 'Failed to reorder top-ups' });
    }
  };

  const startEdit = (topup) => {
    setEditingTopup(topup);
    setFormData({
      name: topup.name || '',
      description: topup.description || '',
      price: topup.price?.toString() || '',
      currency: topup.currency || '₹',
      category: topup.category || 'operations',
      displayOrder: topup.displayOrder?.toString() || '999',
      isActive: topup.isActive !== undefined ? topup.isActive : true,
      popular: topup.popular || false,
      limits: {
        firebase_operations: topup.limits?.firebase_operations || 0,
        storage_mb: topup.limits?.storage_mb || 0,
        sales_documents: topup.limits?.sales_documents || 0,
        parties: topup.limits?.parties || 0
      },
      features: topup.features || []
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      currency: '₹',
      category: 'operations',
      displayOrder: '',
      isActive: true,
      popular: false,
      limits: {
        firebase_operations: 0,
        storage_mb: 0,
        sales_documents: 0,
        parties: 0
      },
      features: []
    });
  };

  const filteredTopups = topups.filter(topup => {
    const matchesCategory = selectedCategory === 'all' || topup.category === selectedCategory;
    const matchesSearch = topup.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         topup.description?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const formatLimits = (limits) => {
    const parts = [];
    if (limits.firebase_operations > 0) parts.push(`${limits.firebase_operations.toLocaleString()} ops`);
    if (limits.storage_mb > 0) {
      const storageGB = limits.storage_mb >= 1024 ? `${(limits.storage_mb / 1024).toFixed(1)} GB` : `${limits.storage_mb} MB`;
      parts.push(`${storageGB} storage`);
    }
    if (limits.sales_documents > 0) parts.push(`${limits.sales_documents.toLocaleString()} docs`);
    if (limits.parties > 0) parts.push(`${limits.parties.toLocaleString()} parties`);
    return parts.join(', ') || 'No limits';
  };

  if (loading && topups.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading top-ups...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Top-up Manager</h1>
          <p className="text-gray-600 mt-1 text-sm sm:text-base">Manage top-up packages and catalog</p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 w-full sm:w-auto"
        >
          <Plus className="w-5 h-5" />
          Add Top-up
        </button>
      </div>


      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search top-ups..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
            />
          </div>
          <div className="w-full sm:w-auto">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
            >
              <option value="all">All Categories</option>
              {categories.map(category => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`p-4 rounded-lg flex items-center gap-2 ${
          message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          <span>{message.text}</span>
        </div>
      )}

      {/* Top-ups List - Desktop Table View */}
      <div className="hidden lg:block bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Order
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Top-up
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Limits
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredTopups.map((topup) => (
                <tr key={topup.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleReorder(topup.id, 'up')}
                        className="p-1 hover:bg-gray-200 rounded"
                        disabled={topup.displayOrder <= 1}
                      >
                        <ArrowUp className="w-4 h-4" />
                      </button>
                      <span className="text-sm font-medium text-gray-900">{topup.displayOrder}</span>
                      <button
                        onClick={() => handleReorder(topup.id, 'down')}
                        className="p-1 hover:bg-gray-200 rounded"
                        disabled={topup.displayOrder >= Math.max(...topups.map(t => t.displayOrder))}
                      >
                        <ArrowDown className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div>
                        <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                          {topup.name}
                          {topup.popular && <Star className="w-4 h-4 text-yellow-500 fill-current" />}
                        </div>
                        <div className="text-sm text-gray-500">{topup.description}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {topup.category}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(topup.price, topup.currency)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatLimits(topup.limits)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleToggleActive(topup)}
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          topup.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {topup.isActive ? <Eye className="w-3 h-3 mr-1" /> : <EyeOff className="w-3 h-3 mr-1" />}
                        {topup.isActive ? 'Active' : 'Inactive'}
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => startEdit(topup)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleTogglePopular(topup)}
                        className={topup.popular ? "text-yellow-600 hover:text-yellow-900" : "text-gray-400 hover:text-yellow-600"}
                      >
                        {topup.popular ? <Star className="w-4 h-4 fill-current" /> : <StarOff className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => handleDeleteTopup(topup.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top-ups List - Mobile Card View */}
      <div className="lg:hidden space-y-4">
        {filteredTopups.map((topup) => (
          <div key={topup.id} className="bg-white rounded-lg shadow p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-sm font-medium text-gray-900">{topup.name}</h3>
                  {topup.popular && <Star className="w-4 h-4 text-yellow-500 fill-current" />}
                </div>
                <p className="text-xs text-gray-500 mb-2">{topup.description}</p>
                <div className="flex items-center gap-2 mb-2">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {topup.category}
                  </span>
                  <span className="text-sm font-medium text-gray-900">
                    {formatCurrency(topup.price, topup.currency)}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mb-3">{formatLimits(topup.limits)}</p>
              </div>
              <div className="flex items-center gap-1 ml-2">
                <button
                  onClick={() => handleReorder(topup.id, 'up')}
                  className="p-1 hover:bg-gray-200 rounded"
                  disabled={topup.displayOrder <= 1}
                >
                  <ArrowUp className="w-3 h-3" />
                </button>
                <span className="text-xs font-medium text-gray-900 mx-1">{topup.displayOrder}</span>
                <button
                  onClick={() => handleReorder(topup.id, 'down')}
                  className="p-1 hover:bg-gray-200 rounded"
                  disabled={topup.displayOrder >= Math.max(...topups.map(t => t.displayOrder))}
                >
                  <ArrowDown className="w-3 h-3" />
                </button>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <button
                onClick={() => handleToggleActive(topup)}
                className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                  topup.isActive
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}
              >
                {topup.isActive ? <Eye className="w-3 h-3 mr-1" /> : <EyeOff className="w-3 h-3 mr-1" />}
                {topup.isActive ? 'Active' : 'Inactive'}
              </button>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => startEdit(topup)}
                  className="text-blue-600 hover:text-blue-900 p-1"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleTogglePopular(topup)}
                  className={topup.popular ? "text-yellow-600 hover:text-yellow-900 p-1" : "text-gray-400 hover:text-yellow-600 p-1"}
                >
                  {topup.popular ? <Star className="w-4 h-4 fill-current" /> : <StarOff className="w-4 h-4" />}
                </button>
                <button
                  onClick={() => handleDeleteTopup(topup.id)}
                  className="text-red-600 hover:text-red-900 p-1"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Create/Edit Form Modal */}
      {(showCreateForm || editingTopup) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  {editingTopup ? 'Edit Top-up' : 'Create New Top-up'}
                </h2>
                <button
                  onClick={() => {
                    setShowCreateForm(false);
                    setEditingTopup(null);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={editingTopup ? handleUpdateTopup : handleCreateTopup} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {categories.map(category => (
                        <option key={category.value} value={category.value}>
                          {category.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Price *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Currency
                    </label>
                    <select
                      value={formData.currency}
                      onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="₹">₹ (INR)</option>
                      <option value="$">$ (USD)</option>
                      <option value="€">€ (EUR)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Display Order
                    </label>
                    <input
                      type="number"
                      value={formData.displayOrder}
                      onChange={(e) => setFormData({ ...formData, displayOrder: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Limits</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Operations
                      </label>
                      <input
                        type="number"
                        value={formData.limits.firebase_operations}
                        onChange={(e) => setFormData({
                          ...formData,
                          limits: { ...formData.limits, firebase_operations: e.target.value }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Storage (MB)
                      </label>
                      <input
                        type="number"
                        value={formData.limits.storage_mb}
                        onChange={(e) => setFormData({
                          ...formData,
                          limits: { ...formData.limits, storage_mb: e.target.value }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Documents
                      </label>
                      <input
                        type="number"
                        value={formData.limits.sales_documents}
                        onChange={(e) => setFormData({
                          ...formData,
                          limits: { ...formData.limits, sales_documents: e.target.value }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Parties
                      </label>
                      <input
                        type="number"
                        value={formData.limits.parties}
                        onChange={(e) => setFormData({
                          ...formData,
                          limits: { ...formData.limits, parties: e.target.value }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-6">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Active</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.popular}
                      onChange={(e) => setFormData({ ...formData, popular: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Popular</span>
                  </label>
                </div>

                <div className="flex justify-end gap-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateForm(false);
                      setEditingTopup(null);
                      resetForm();
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    <Save className="w-4 h-4" />
                    {editingTopup ? 'Update' : 'Create'} Top-up
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TopupManager;